import {
  ALL_FORMATS,
  AudioBufferSource,
  BlobSource,
  BufferTarget,
  Conversion,
  Input,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  WebMOutputFormat,
} from "mediabunny";
import { toAudioBuffer } from "../audio/audioBuffer";
import type { PcmAudio } from "../types";
import type { ConvertedVideo, VideoContainer } from "./convertVideo";

export interface OverlayFrame {
  /** Source timestamp of this frame, in seconds (trim is already accounted for). */
  time: number;
  width: number;
  height: number;
}

export interface RenderWithOverlayOptions {
  format: VideoContainer;
  /** Keep only `[start, end]` (seconds) — e.g. the clip editor's in/out. */
  trim?: { start: number; end: number };
  onProgress?: (progress: number) => void;
  /** Abort the encode; `execute()` rejects with mediabunny's ConversionCanceledError. */
  signal?: AbortSignal;
  /**
   * Replace the input's audio with this PCM track. When set, all source audio
   * tracks are discarded and this is muxed as the only output audio track.
   */
  replacementAudio?: PcmAudio;
  /**
   * Draw on top of each frame. The 2D `ctx` already has the decoded video frame
   * drawn; return value is ignored (mutate `ctx`). `frame.time` is the **source**
   * timestamp in seconds, so callers can look things up against the original
   * timeline even when trimming.
   */
  drawOverlay: (ctx: OffscreenCanvasRenderingContext2D, frame: OverlayFrame) => void;
}

async function waitForOutputStart(
  output: { state: "pending" | "started" | "canceled" | "finalizing" | "finalized" },
  signal?: AbortSignal
): Promise<void> {
  while (output.state === "pending") {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  if (output.state !== "started") {
    throw new Error("Could not add replacement audio because the output never started.");
  }
}

async function addReplacementAudio(
  source: AudioBufferSource,
  output: { state: "pending" | "started" | "canceled" | "finalizing" | "finalized" },
  audio: PcmAudio,
  signal?: AbortSignal
): Promise<void> {
  await waitForOutputStart(output, signal);
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  await source.add(toAudioBuffer(audio));
  source.close();
}

/**
 * Re-encode a video while compositing a custom 2D overlay onto every frame —
 * the generic primitive behind burned-in captions, watermarks, etc. mediabunny's
 * `Conversion` does decode/encode/mux on hardware WebCodecs; audio is copied
 * through. Codec-generic: it knows nothing about captions or styles — the caller
 * supplies `drawOverlay`. Browser-only.
 */
export async function renderWithOverlay(
  file: Blob,
  options: RenderWithOverlayOptions
): Promise<ConvertedVideo> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  const isWebm = options.format === "webm";
  const output = new Output({
    format: isWebm ? new WebMOutputFormat() : new Mp4OutputFormat(),
    target: new BufferTarget(),
  });
  const replacementAudioSource = options.replacementAudio
    ? new AudioBufferSource({
        codec: isWebm ? "opus" : "aac",
        bitrate: QUALITY_HIGH,
      })
    : null;
  if (replacementAudioSource) {
    output.addAudioTrack(replacementAudioSource);
  }

  // mediabunny's `process` reports trim-rebased (output) timestamps; shift back
  // to source time so overlays line up with the original timeline.
  const trimStart = options.trim?.start ?? 0;
  let ctx: OffscreenCanvasRenderingContext2D | null = null;

  const conversion = await Conversion.init({
    input,
    output,
    ...(options.trim ? { trim: options.trim } : {}),
    ...(replacementAudioSource ? { audio: { discard: true } } : {}),
    video: {
      bitrate: QUALITY_HIGH,
      process: (sample) => {
        const w = sample.displayWidth;
        const h = sample.displayHeight;
        if (!ctx) {
          ctx = new OffscreenCanvas(w, h).getContext("2d") as OffscreenCanvasRenderingContext2D;
        }
        ctx.clearRect(0, 0, w, h);
        sample.draw(ctx, 0, 0);
        options.drawOverlay(ctx, { time: sample.timestamp + trimStart, width: w, height: h });
        return ctx.canvas;
      },
    },
  });

  if (!conversion.isValid) {
    throw new Error("This video can't be processed in the browser (no convertible tracks).");
  }
  if (options.onProgress) conversion.onProgress = options.onProgress;

  // Cancellation: mediabunny's `cancel()` makes the in-flight `execute()` reject
  // with a ConversionCanceledError, which the caller distinguishes from a failure.
  if (options.signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const onAbort = () => void conversion.cancel();
  options.signal?.addEventListener("abort", onAbort, { once: true });

  try {
    const execute = conversion.execute();
    const audio =
      replacementAudioSource && options.replacementAudio
        ? addReplacementAudio(
            replacementAudioSource,
            output,
            options.replacementAudio,
            options.signal
          )
        : null;
    await Promise.all([
      execute,
      audio?.catch((error) => {
        void conversion.cancel();
        throw error;
      }),
    ]);
  } finally {
    options.signal?.removeEventListener("abort", onAbort);
    input.dispose();
  }

  const data = output.target.buffer;
  if (!data) throw new Error("Rendering produced no output.");
  return {
    data,
    mimeType: isWebm ? "video/webm" : "video/mp4",
    extension: isWebm ? "webm" : "mp4",
  };
}
